import { Helmet } from "react-helmet-async";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogType?: "website" | "article";
  noIndex?: boolean;
  schemaJson?: Record<string, unknown> | Record<string, unknown>[];
}

const SITE_NAME = "heilys.info";
const DEFAULT_OG_IMAGE = "https://heilys.info/images/hero-main.webp";
const DEFAULT_OG_IMAGE_ALT =
  "heilys Rabattcode LONGEVITY – 5 % Rabatt auf Rotlichttherapie-Geräte";

// DACH-weite hreflang-Strategie: einsprachig deutsch, identische Inhalte für
// DE/AT/CH. x-default als Fallback für unbekannte Locales.
const HREFLANG_LOCALES = ["de", "de-DE", "de-AT", "de-CH", "x-default"] as const;

const SEOHead = ({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt = DEFAULT_OG_IMAGE_ALT,
  ogType = "website",
  noIndex = false,
  schemaJson,
}: SEOHeadProps) => {
  const schemas = schemaJson
    ? Array.isArray(schemaJson)
      ? schemaJson
      : [schemaJson]
    : [];

  return (
    <Helmet>
      <html lang="de" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      <link rel="canonical" href={canonical} />

      {/* hreflang – einsprachig deutsch für DACH */}
      {HREFLANG_LOCALES.map((locale) => (
        <link key={locale} rel="alternate" hrefLang={locale} href={canonical} />
      ))}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="de_DE" />
      {/* Hinweis: og:locale:alternate ist hier bewusst weggelassen –
          Helmet dedupliziert Meta nach property, mehrere Werte würden kollidieren. */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="800" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;
